import { Component, ElementRef, Input, ViewChild, forwardRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

interface OptionView {
  value: string;
  group: 'recent' | 'pinned' | 'main' | 'unknown';
  /** The label split around the search terms, for bolding the matches */
  parts: { text: string; hit: boolean }[];
}

let nextId = 0;

/**
 * A dropdown you can type into, for long lists (e.g. 50+ interactables): typing filters the
 * list (every word must appear, any case), arrows move, Enter picks, Esc puts the value back.
 * Only values from the lists can be picked. `pinned` options (e.g. built-in types) are listed
 * first under their own heading; a current value in neither list is shown with `unknownNote`.
 * With `recentKey`, the last few picks (kept in this browser under that key) are listed at the
 * top under "Recent" until you start typing.
 * Works as a form control (formControlName / ngModel).
 */
@Component({
  selector: 'aa-search-select',
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchSelectComponent), multi: true }],
})
export class SearchSelectComponent implements ControlValueAccessor {
  @Input() options: string[] = [];
  @Input() pinned: string[] = [];
  @Input() pinnedLabel = 'Built-in';
  @Input() mainLabel = 'Options';
  /** Shown after a current value that's in neither list, e.g. "(not in module)" */
  @Input() unknownNote = '';
  /** Optional muted note per option, e.g. how many steps use it */
  @Input() notes: Record<string, string> = {};
  @Input() placeholder = 'Search…';
  @Input() inputId = '';
  @Input() loading = false;
  /** Remembers the last picks under this key and lists them first; '' turns Recent off */
  @Input() recentKey = '';
  @Input() recentLimit = 5;

  @ViewChild('input') private input?: ElementRef<HTMLInputElement>;
  @ViewChild('list') private list?: ElementRef<HTMLElement>;

  readonly listId = `aa-search-select-list-${nextId++}`;
  value = '';
  query = '';
  open = false;
  disabled = false;
  highlighted = -1;
  /** Most recent first, as stored; only the ones still in the lists are shown */
  private recents: string[] = [];

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly cdr: ChangeDetectorRef) {}

  // ----- ControlValueAccessor -----
  writeValue(value: string | null): void {
    this.value = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) this.close();
  }

  // ----- What's listed -----
  /** The value shown in the box: the search while open, the picked value otherwise */
  get display(): string {
    return this.open ? this.query : this.value;
  }

  get unknownValue(): string | null {
    return this.value && !this.pinned.includes(this.value) && !this.options.includes(this.value) ? this.value : null;
  }

  get filtered(): OptionView[] {
    const terms = this.query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const views: OptionView[] = [];
    const add = (value: string, group: OptionView['group']) => {
      const lower = value.toLowerCase();
      if (terms.every((t) => lower.includes(t))) views.push({ value, group, parts: highlight(value, terms) });
    };
    // Recent only while nothing's typed, so a search lists each match once
    if (!terms.length) {
      this.recents.filter((v) => this.pinned.includes(v) || this.options.includes(v)).slice(0, this.recentLimit).forEach((v) => add(v, 'recent'));
    }
    const unknown = this.unknownValue;
    if (unknown) add(unknown, 'unknown');
    this.pinned.forEach((v) => add(v, 'pinned'));
    this.options.filter((v) => !this.pinned.includes(v)).forEach((v) => add(v, 'main'));
    return views;
  }

  get mainCount(): number {
    return this.options.filter((v) => !this.pinned.includes(v)).length;
  }

  // ----- Opening and picking -----
  openList(): void {
    if (this.disabled || this.open) return;
    this.open = true;
    this.query = '';
    this.recents = this.loadRecents();
    this.highlighted = Math.max(0, this.filtered.findIndex((o) => o.value === this.value));
    setTimeout(() => {
      this.input?.nativeElement.select();
      this.scrollHighlightedIntoView();
    });
  }

  close(): void {
    this.open = false;
    this.query = '';
    this.highlighted = -1;
  }

  onInput(text: string): void {
    if (!this.open) this.openList();
    this.query = text;
    this.highlighted = this.filtered.length ? 0 : -1;
  }

  onKeydown(event: KeyboardEvent): void {
    const options = this.filtered;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!this.open) {
          this.openList();
          return;
        }
        if (!options.length) return;
        this.highlighted = event.key === 'ArrowDown'
          ? (this.highlighted + 1) % options.length
          : (this.highlighted - 1 + options.length) % options.length;
        this.scrollHighlightedIntoView();
        break;
      case 'Enter':
        if (!this.open) return;
        event.preventDefault();
        if (options[this.highlighted]) this.pick(options[this.highlighted].value);
        break;
      case 'Escape':
        if (!this.open) return;
        event.preventDefault();
        event.stopPropagation();
        this.close();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  /** Clicking away keeps the value as it was */
  onBlur(): void {
    this.close();
    this.onTouched();
  }

  pick(value: string): void {
    this.rememberRecent(value);
    if (value !== this.value) {
      this.value = value;
      this.onChange(value);
    }
    this.onTouched();
    this.close();
  }

  private storageKey(): string {
    return `aa-search-select-recent:${this.recentKey}`;
  }

  private loadRecents(): string[] {
    if (!this.recentKey) return [];
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey()) || '[]');
      return Array.isArray(saved) ? saved.filter((v) => typeof v === 'string') : [];
    } catch {
      return [];
    }
  }

  private rememberRecent(value: string): void {
    if (!this.recentKey || !value) return;
    this.recents = [value, ...this.loadRecents().filter((v) => v !== value)].slice(0, Math.max(this.recentLimit, 20));
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(this.recents));
    } catch {
      // Storage blocked: Recent just isn't remembered
    }
  }

  optionId(index: number): string {
    return `${this.listId}-${index}`;
  }

  private scrollHighlightedIntoView(): void {
    setTimeout(() => {
      this.list?.nativeElement.querySelector<HTMLElement>(`#${this.optionId(this.highlighted)}`)?.scrollIntoView({ block: 'nearest' });
    });
  }
}

/** The text split into pieces, with every occurrence of a search term marked */
function highlight(text: string, terms: string[]): { text: string; hit: boolean }[] {
  if (!terms.length) return [{ text, hit: false }];
  const lower = text.toLowerCase();
  const marks = new Array(text.length).fill(false);
  for (const term of terms) {
    let at = lower.indexOf(term);
    while (at !== -1) {
      for (let i = at; i < at + term.length; i++) marks[i] = true;
      at = lower.indexOf(term, at + term.length);
    }
  }
  const parts: { text: string; hit: boolean }[] = [];
  for (let i = 0; i < text.length; i++) {
    const last = parts[parts.length - 1];
    if (last && last.hit === marks[i]) last.text += text[i];
    else parts.push({ text: text[i], hit: marks[i] });
  }
  return parts;
}
